import React, { useEffect, useState, memo, useMemo, useRef } from "react";
import {
  Box,
  Button,
  Stack,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Checkbox,
  IconButton,
  TablePagination,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Table,
  TableBody,
} from "@mui/material";

import {
  CustomTitle,
  Loader,
  getYesterdayDate,
  handleSessionOut,
} from "../../HelperFunctions/HelperFunctions";
import DeleteIcon from "@mui/icons-material/Delete";
import SimCardDownloadOutlinedIcon from "@mui/icons-material/SimCardDownloadOutlined";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import {
  getCustomeReportTableData,
  getUserEditedData,
  deleteCustomeReport,
  downloadCustomeReport,
  manualSendReport,
  get_report_schedule,
} from "../../API/backend";
import { enqueueSnackbar } from "notistack";
import UpdateScheduleDialog from "../../Components/UpdateScheduleDialog";
import SchedulerDialog from "../../Components/SchedulerDialog";
import { useLoaderData } from "react-router-dom";
// import { Table, Column, HeaderCell, Cell } from 'rsuite-table';
import { getSubUserListByUserId } from "../../API/nimbleAPI";
import { Input, InputGroup, Pagination } from "rsuite";
// import "rsuite/dist/rsuite.css";
import "rsuite/dist/rsuite.min.css";

const CustomReportsScheduler = ({
  refresh,
  setRefresh,
  onSubmitSuccess,
  name,
}) => {
  const {
    privileges = {},
    clientID,
    clientName,
    UID,
    token,
    userPreferences,
  } = useLoaderData();
  const { Create = false, Update = false, Delete = false } = privileges;

  const [action, setAction] = useState("none");
  const [loading, setLoading] = useState(false);
  const [customReportData, setCustomeReportData] = useState([]);
  const [updateScheduleData, setUpdateScheduleData] = useState(null);
  const [editDialogData, setEditDialogdata] = useState(null);
  const [editCustomData, setEditCustomData] = useState(null);

  const [selectedRows, setSelectedRows] = useState([]);
  const [userName, setUserName] = useState(null);

  const rowsPerPage = userPreferences?.ViewGridPageSize;
  // const rowsPerPage = 2;
  const [page, setPage] = useState(0);
  const [goToPage, setGoToPage] = useState(1);

  const total = customReportData.length;
  const totalPages = Math.ceil(total / rowsPerPage);
  const tableContainerRef = useRef(null);

  const handleGo = () => {
    const userPage = parseInt(goToPage, 10);
    if (!isNaN(userPage) && userPage >= 1 && userPage <= totalPages) {
      setPage(userPage - 1);
      // setGoToPage('');
    } else {
      enqueueSnackbar("Number cannot be greater than available pages", {
        variant: "warning",
      });
    }
  };

  useEffect(() => {
    fetchCustomeReportTableData();
  }, []);

  useEffect(() => {
    if (refresh) {
      fetchCustomeReportTableData();
      setRefresh(false);
    }
  }, [refresh]);

  const fetchCustomeReportTableData = async () => {
    setLoading(true);

    try {
      const userIds = [];

      const getUserCorporations = await getSubUserListByUserId();
      Object.values(getUserCorporations).forEach((user) => {
        userIds.push(user.User_Id);
        if (user.User_Id === UID) {
          const getName = `${user.FirstName}_${user.LastName}`;
          setUserName(getName);
          name(getName);
        }
      });
      const getTableData = await getCustomeReportTableData(userIds);
       setCustomeReportData(getTableData);
    } catch (error) {
      if (error.status == 404) {
        enqueueSnackbar(`${error.response.data.detail}`, { variant: "info" });
        return;
      } else if (error.status == 400) {
        return;
      } else if (error.status === 401) {
        await handleSessionOut();
      }
      enqueueSnackbar("Error while fetching the data", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return customReportData.slice(start, end);
  }, [customReportData, page, rowsPerPage]);

  const handleSelectAll = async (event, currentPageRows) => {
    const yesterdayDate = getYesterdayDate();
    if (event.target.checked) {
      const pageRows = currentPageRows.map((row) => ({
        schedule_id: row.schedule_id,
        date: yesterdayDate,
        report_book: row.report_book_name,
      }));

      setSelectedRows((prev) => {
        const newRows = pageRows.filter(
          (row) => !prev.some((item) => item.schedule_id === row.schedule_id),
        );
        return [...prev, ...newRows];
      });
    } else {
      setSelectedRows((prev) =>
        prev.filter(
          (item) =>
            !currentPageRows.some(
              (row) => row.schedule_id === item.schedule_id,
            ),
        ),
      );
    }
  };

  const handleSelectRow = async (row) => {
    const yesterdayDate = getYesterdayDate();

    setSelectedRows((prev) => {
      const exists = prev.find((item) => item.schedule_id === row.schedule_id);
      if (exists) {
        return prev.filter((item) => item.schedule_id !== row.schedule_id);
      }
      return [
        ...prev,
        {
          schedule_id: row.schedule_id,
          date: yesterdayDate,
          report_book: row.report_book_name,
        },
      ];
    });
  };

  const handleBulkSend = async (e) => {
    const value = e.target.value;
    setAction(value);

    if (value === "send") {
      if (selectedRows.length < 2) {
        Update
          ? enqueueSnackbar("Select at least 2 schedules", {
              variant: "warning",
            })
          : enqueueSnackbar("You don't have update privileges", {
              variant: "warning",
            });
        setAction("none");
        return;
      }
    }

    try {
      setLoading(true);

      if (!Update) {
        enqueueSnackbar("You don't have update privileges", {
          variant: "warning",
        });
        return;
      }

      const reSend = await manualSendReport(selectedRows);
      if (reSend.status === 200) {
        const results = reSend.data.results;

        const allTrue = results.every((item) => item.is_sent === true);
        const allFalse = results.every((item) => item.is_sent === false);

        if (allTrue) {
          enqueueSnackbar("sent successfully", { variant: "success" });
        } else {
          enqueueSnackbar("Error while send", { variant: "error" });
        }
        setRefresh(true);
      }
    } catch (error) {
      if (error.status === 404) {
        enqueueSnackbar(`${error?.response?.data?.detail}`, {
          variant: "warning",
        });
      } else if (error.status === 401) {
        await handleSessionOut();
      } else {
        enqueueSnackbar("Error while send", { variant: "error" });
      }
    } finally {
      setLoading(false);
      setAction("none");
      setSelectedRows([]);
    }
  };

  const manualSend = async (data) => {
    const yesterdayDate = await getYesterdayDate();
    const payload = [
      {
        schedule_id: data.schedule_id,
        date: yesterdayDate,
        report_book: data.report_book_name,
      },
    ];
    try {
      setLoading(true);
      const reSend = await manualSendReport(payload);
      if (reSend.status === 200) {
        const results = reSend?.data?.results[0]?.is_sent;
        if (results) {
          setRefresh(true);
          enqueueSnackbar("sent successfully", { variant: "success" });
        } else {
          enqueueSnackbar("Error while send", { variant: "error" });
        }
      }
    } catch (error) {
      if (error.status === 404) {
        enqueueSnackbar(`${error?.response?.data?.detail}`, {
          variant: "warning",
        });
      } else if (error.status === 401) {
        await handleSessionOut();
      } else {
        enqueueSnackbar("Error while send", { variant: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  // const getEditData = async (data) => {
  //   try {
  //     setLoading(true);
  //     const { schedule_id } = data;

  //     const userEditedData = await getUserEditedData(schedule_id);
  //     if (userEditedData.status == 200) {
  //       setEditDialogdata(userEditedData.data);
  //     }
  //     const reportScheduleData = await get_report_schedule(schedule_id);
  //     const keyMap = {
  //        Daily Sale Flash Report: " Daily Sale Flash Report",
  //       payment_log_report: "Payment Log Report",
  //     };
  //     const raw = reportScheduleData?.customizations || {};
  //     setEditCustomData({
  //       ...reportScheduleData,
  //       customizations: Object.fromEntries(
  //         Object.entries(raw)
  //           .filter(([, v]) => v != null)
  //           .map(([k, v]) => [keyMap[k] || k, v]),
  //       ),
  //     });
  //   } catch (error) {
  //     if (error.status === 401) {
  //       await handleSessionOut();
  //     }
  //     enqueueSnackbar("Error while edit", { variant: "error" });
  //   } finally {
  //     setLoading(false);
  //   }
  // };
  const getEditData = async (data) => {
    try {
      setLoading(true);

      const { schedule_id } = data;

      // Metadata from FastAPI get_edit_schedule (not AWS /schedule).
      const userEditedData = await getUserEditedData(schedule_id);
      // Per-report customizations from schedule_customizations.
      const reportScheduleData = await get_report_schedule(schedule_id);

      if (userEditedData.status === 200) {
        setEditDialogdata(userEditedData.data);
      }

      const customizations = reportScheduleData?.customizations || {};

      setEditCustomData({
        ...reportScheduleData,
        customizations: {
          "Daily Sale Flash Report": customizations["Daily Sale Flash Report"],
          "Payment Log Report":
            customizations["Payment Log Report"] ||
            customizations.payment_log_report,
          "Restaurant Flash Report": customizations["Restaurant Flash Report"],
        },
      });
    } catch (error) {
      if (error.status === 401) {
        await handleSessionOut();
      }

      enqueueSnackbar("Error while edit", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteReport = async (data) => {
    setLoading(true);
    try {
      const { schedule_id } = data;
      const deleteData = await deleteCustomeReport(schedule_id);
      if (deleteData.status === 200) {
        setCustomeReportData((prevData) =>
          prevData.filter((row) => row.schedule_id !== data.schedule_id),
        );
        enqueueSnackbar("Deleted Successfully", { variant: "success" });
      }
    } catch (error) {
      if (error.status === 401) {
        await handleSessionOut();
      } else {
        enqueueSnackbar("Delete report failed", { variant: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async (data) => {
    setLoading(true);
    try {
      const { schedule_id } = data;

      // Omit date → API returns the last successfully sent report(s).
      const downloadStatus = await downloadCustomeReport(
        clientName,
        schedule_id,
      );

      const blob = new Blob([downloadStatus.data], {
        type: downloadStatus.headers["content-type"],
      });

      const contentDisposition = downloadStatus.headers["content-disposition"];
      const filename =
        downloadStatus.headers["x-file-name"] ||
        downloadStatus.headers["x-filename"] ||
        "Daily Sales Flash Report_downloaded";

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      enqueueSnackbar("Download Successfully", { variant: "success" });
    } catch (error) {
      const status = error?.response?.status ?? error?.status;
      // downloadCustomeReport uses responseType: arraybuffer, so error JSON
      // arrives as bytes — decode before reading detail.
      let detail = "";
      const raw = error?.response?.data;
      try {
        if (raw instanceof ArrayBuffer) {
          detail = JSON.parse(new TextDecoder().decode(raw))?.detail || "";
        } else if (typeof raw === "string") {
          detail = JSON.parse(raw)?.detail || raw;
        } else if (raw && typeof raw === "object") {
          detail = raw.detail || "";
        }
      } catch {
        detail = "";
      }

      if (status === 404) {
        enqueueSnackbar(
          detail || "Report not generated for your schedule yet",
          { variant: "warning" },
        );
      } else if (status === 401) {
        await handleSessionOut();
      } else {
        enqueueSnackbar(detail || "Download report failed", {
          variant: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { dataKey: "report_name", columnName: "Report Name", width: "170px" },
    {
      dataKey: "report_book_name",
      columnName: "Report Book Name",
      width: "170px",
    },
    { dataKey: "range", columnName: "Range", width: "80px" },
    {
      dataKey: "no_of_corporations",
      columnName: "No of Corporations",
      width: "80px",
    },
    { dataKey: "no_of_users", columnName: "No of Users", width: "70px" },
    // { name: "report_month", width: "80px" },
    {
      dataKey: "last_modified_by",
      columnName: "Created/Modified By",
      width: "100px",
    },
    { dataKey: "last_sent", columnName: "Last Sent", width: "120px" },
  ];
  // const ValueCell = memo(({ value }) => {
  //   const displayValue =
  //     typeof value === "object" && value !== null
  //       ? value.day || "-"
  //       : value || "-";
  //   return <span title={displayValue}>{displayValue}</span>;
  // });

  const ValueCell = memo(({ value, title }) => {
    const displayValue =
      typeof value === "object" && value !== null
        ? value.day || "-"
        : value || "-";
 

    return <span title={title || displayValue}>{displayValue}</span>;
  });

  const ActionButtons = memo(({ rowData }) => (
    <Stack direction="row">
      <span title="Edit">
        <IconButton
          size="small"
          onClick={() =>
            Update
              ? getEditData(rowData)
              : enqueueSnackbar("You don't have update privileges", {
                  variant: "warning",
                })
          }
        >
          <BorderColorOutlinedIcon sx={{ color: "#00efd1" }} />
        </IconButton>
      </span>

      <span title="Download">
        <IconButton size="small" onClick={() => downloadReport(rowData)}>
          <SimCardDownloadOutlinedIcon sx={{ color: "#00efd1" }} />
        </IconButton>
      </span>
      <span title="Delete">
        <IconButton
          size="small"
          onClick={() => {
            if (Delete) {
              const confirmDelete = window.confirm(
                "Would you like to delete the scheduled report?",
              );
              if (confirmDelete) {
                deleteReport(rowData);
              }
            } else {
              enqueueSnackbar("You don't have delete privileges", {
                variant: "warning",
              });
            }
          }}
        >
          <DeleteIcon sx={{ color: "#00efd1" }} />
        </IconButton>
      </span>
    </Stack>
  ));

  const isSelected = (id) => selectedRows.some((row) => row.schedule_id === id);

  return (
    <>
      <Stack direction="column" gap={2} height={"100%"}>
        <FormControl sx={{ width: 150 }} style={{ marginTop: "7px" }}>
          <InputLabel id="bulk-action-label">Bulk Action</InputLabel>
          <Select
            size="small"
            value={action}
            label="Bulk Action"
            onChange={handleBulkSend}
          >
            <MenuItem value="none">None</MenuItem>
            <MenuItem value="send">Send</MenuItem>
          </Select>
        </FormControl>

        <Box height="100%" display="flex" flexDirection="column" minHeight={0}>
          <TableContainer ref={tableContainerRef} sx={{ overflow: "auto" }}>
            <Table stickyHeader size="small" sx={{ width: "100%" }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#d7d7d7" }}>
                  <TableCell
                    padding="checkbox"
                    sx={{
                      borderRight: "1px solid white",
                      backgroundColor: "#d7d7d7",
                    }}
                  >
                    <Checkbox
                      size="small"
                      color="default"
                      disableFocusRipple
                      disableRipple
                      disableTouchRipple
                      checked={
                        paginatedData.length > 0 &&
                        paginatedData.every((row) =>
                          isSelected(row.schedule_id),
                        )
                      }
                      indeterminate={
                        paginatedData.some((row) =>
                          isSelected(row.schedule_id),
                        ) &&
                        !paginatedData.every((row) =>
                          isSelected(row.schedule_id),
                        )
                      }
                      onChange={(e) => handleSelectAll(e, paginatedData)}
                    />
                  </TableCell>

                  {columns.map((col, idx) => (
                    <TableCell
                      key={idx}
                      style={{
                        backgroundColor: "#d7d7d7",
                        borderRight: "1px solid white",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        fontSize: 13,
                        maxWidth: col.width,
                      }}
                    >
                      <span title={col.columnName}> {col.columnName} </span>
                    </TableCell>
                  ))}
                  {["Send", "Schedule", "Actions"].map((label, idx) => (
                    <TableCell
                      key={idx}
                      style={{
                        backgroundColor: "#d7d7d7",
                        borderRight: "1px solid white",
                        fontSize: 13,
                      }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedData.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length + 4}
                      align="center"
                      sx={{ fontSize: 14, color: "#999" }}
                    >
                      No data available
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((row, rowIndex) => (
                    <TableRow key={row.schedule_id} hover>
                      <TableCell
                        padding="checkbox"
                        sx={{ borderRight: "1px solid #f2f2f2" }}
                      >
                        <Checkbox
                          size="small"
                          color="default"
                          checked={isSelected(row.schedule_id)}
                          onChange={() => handleSelectRow(row)}
                          disableFocusRipple
                          disableRipple
                          disableTouchRipple
                        />
                      </TableCell>

                      {columns.map((col, idx) => (
                        <TableCell
                          key={idx}
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            fontSize: 12,
                            fontWeight: 500,
                            color: "#626262",
                            borderRight: "1px solid #f2f2f2",
                            maxWidth: col.width,
                          }}
                        >
                          {/* <ValueCell
                            value={
                              col.dataKey === "last_modified_by"
                                ? CustomTitle(row[col.dataKey])
                                : Array.isArray(row[col.dataKey])
                                  ? row[col.dataKey].join(", ")
                                  : row[col.dataKey]
                            }
                          /> */}
                          <ValueCell
                            value={
                              col.dataKey === "range"
                                ? row.range || "-"
                                : col.dataKey === "report_name" &&
                                    Array.isArray(row[col.dataKey])
                                  ? `${row[col.dataKey][0]}${
                                      row[col.dataKey].length > 1
                                        ? `, ${row[col.dataKey][1].slice(0, 5)}...${
                                            row[col.dataKey].length > 2
                                              ? ` +${row[col.dataKey].length - 2}`
                                              : ""
                                          }`
                                        : ""
                                    }`
                                  : col.dataKey === "last_modified_by"
                                    ? CustomTitle(row[col.dataKey])
                                    : Array.isArray(row[col.dataKey])
                                      ? row[col.dataKey].join(", ")
                                      : row[col.dataKey]
                            }
                            title={
                              col.dataKey === "report_name" &&
                              Array.isArray(row[col.dataKey])
                                ? row[col.dataKey].join(", ")
                                : undefined
                            }
                          />
                        </TableCell>
                      ))}

                      <TableCell
                        sx={{
                          borderRight: "1px solid #f2f2f2",
                          width: "80px",
                        }}
                      >
                        <Button
                          variant="contained"
                          size="small"
                          disableElevation
                          onClick={() =>
                            Update
                              ? manualSend(row)
                              : enqueueSnackbar(
                                  "You don't have update privileges",
                                  { variant: "warning" },
                                )
                          }
                          sx={{
                            textTransform: "none",
                            bgcolor: "#fbdbb1",
                            color: "#b8741a",
                            fontWeight: 700,
                            fontSize: "10px",
                            minWidth: "105px",
                          }}
                        >
                          Manual Send
                        </Button>
                      </TableCell>

                      <TableCell
                        sx={{ borderRight: "1px solid #f2f2f2", width: "80px" }}
                      >
                        <Button
                          variant="contained"
                          size="small"
                          disableElevation
                          onClick={() =>
                            Update
                              ? setUpdateScheduleData(row)
                              : enqueueSnackbar(
                                  "You don't have update privileges",
                                  { variant: "warning" },
                                )
                          }
                          sx={{
                            textTransform: "none",
                            backgroundColor: "#82ffff",
                            color: "#067db4",
                            fontWeight: 700,
                            fontSize: "10px",
                            minWidth: "110px",
                          }}
                        >
                          Update Schedule
                        </Button>
                      </TableCell>

                      <TableCell
                        sx={{ borderRight: "1px solid #f2f2f2", width: "80px" }}
                      >
                        <ActionButtons rowData={row} />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            border={"1px solid #f2f2f2"}
            display={"flex"}
            justifyContent={"flex-end"}
            alignItems={"center"}
            gap={"12px"}
          >
            <Pagination
              total={total}
              limit={rowsPerPage}
              activePage={page + 1}
              onChangePage={(val) => {
                setPage(val - 1);
                setGoToPage(val);

                setTimeout(() => {
                  if (tableContainerRef.current) {
                    tableContainerRef.current.scrollTop = 0;
                  }
                }, 0);
              }}
              maxButtons={0.5}
              // layout={['first', 'prev', '-', 'next', 'last']}
              size="sm"
              prev
              next
              first
              last
            />
            <span style={{ fontSize: "14px" }}>
              {totalPages === 0 ? 0 : page + 1} of {totalPages}
            </span>
            <InputGroup size="sm" style={{ width: 100 }}>
              <Input
                value={goToPage}
                onChange={(value) => setGoToPage(value)}
                type="number"
                min={1}
                max={totalPages}
              />
              <InputGroup.Button
                style={{ backgroundColor: "#b5dcf2" }}
                onClick={handleGo}
              >
                Go
              </InputGroup.Button>
            </InputGroup>
          </Box>
        </Box>
      </Stack>

      <Loader loading={loading} />
      {Boolean(updateScheduleData) && (
        <UpdateScheduleDialog
          open={Boolean(updateScheduleData)}
          onClose={() => setUpdateScheduleData(null)}
          reportData={updateScheduleData}
          userName={userName}
          setRefresh={setRefresh}
        />
      )}

      {Boolean(editDialogData) && (
        <SchedulerDialog
          open={Boolean(editDialogData)}
          onClose={() => setEditDialogdata(null)}
          onSubmitSuccess={onSubmitSuccess}
          data={editDialogData}
          isEdit={true}
          userName={userName}
          editCustomData={editCustomData}
        />
      )}
    </>
  );
};

export default CustomReportsScheduler;
